import React, { useEffect, useMemo, useState } from 'react';

export default function ChairmanHome({ token }) {
  const [rows, setRows] = useState([]);
  const [loadingRegs, setLoadingRegs] = useState(false);
  const [errorRegs, setErrorRegs] = useState('');

  const [financeData, setFinanceData] = useState({ headers: [], rows: [] });
  const [loadingFin, setLoadingFin] = useState(false);
  const [errorFin, setErrorFin] = useState('');

  const fetchRegs = async () => {
    setErrorRegs('');
    setLoadingRegs(true);
    try {
      const res = await fetch('/.netlify/functions/admin-get-chairman-registrations', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const raw = await res.text();
      let json = {};
      try { json = raw ? JSON.parse(raw) : {}; } catch { json = { error: raw }; }
      if (!res.ok) {
        setErrorRegs(json?.error || `Failed to load registrations: ${res.status}`);
        setRows([]);
        return;
      }
      setRows(Array.isArray(json.rows) ? json.rows : []);
    } catch {
      setErrorRegs('Network error while loading registrations');
      setRows([]);
    } finally {
      setLoadingRegs(false);
    }
  };

  const fetchFinance = async () => {
    setErrorFin('');
    setLoadingFin(true);
    try {
      const res = await fetch('/.netlify/functions/admin-get-finance', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const raw = await res.text();
      let json = {};
      try { json = raw ? JSON.parse(raw) : {}; } catch { json = { error: raw }; }
      if (!res.ok) {
        setErrorFin(json?.error || `Failed to load finance: ${res.status}`);
        setFinanceData({ headers: [], rows: [] });
        return;
      }
      setFinanceData({ headers: json.headers || [], rows: Array.isArray(json.rows) ? json.rows : [] });
    } catch {
      setErrorFin('Network error while loading finance');
      setFinanceData({ headers: [], rows: [] });
    } finally {
      setLoadingFin(false);
    }
  };

  useEffect(() => {
    if (!token) return;
    fetchRegs();
    fetchFinance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const stats = useMemo(() => {
    const totalRegistered = rows.length;
    const needAssist = rows.filter((r) => String(r.needsAssistance || '').toLowerCase() === 'yes').length;

    const toNumber = (v) => {
      const n = Number(String(v ?? '').replace(/[^0-9.-]/g, ''));
      return Number.isFinite(n) ? n : 0;
    };

    const finRows = Array.isArray(financeData?.rows) ? financeData.rows : [];
    const totalIncome = finRows
      .filter((r) => String(r?.type || '').toLowerCase() === 'income')
      .reduce((sum, r) => sum + toNumber(r.amount), 0);

    const totalExpense = finRows
      .filter((r) => String(r?.type || '').toLowerCase() === 'expense')
      .reduce((sum, r) => sum + toNumber(r.amount), 0);

    const net = totalIncome - totalExpense;

    const fmtUsd = (n) =>
      new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);

    return { totalRegistered, needAssist, totalIncome, totalExpense, net, fmtUsd };
  }, [rows, financeData]);

  return (
    <div style={{ marginTop: 12, display: 'grid', gap: 12 }}>
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'baseline' }}>
          <div>
            <h2 style={{ margin: 0 }}>Camp Fusion Overview</h2>
            <div className="muted" style={{ marginTop: 4 }}>
              Quick updates: registrations, assistance requests, and finances.
            </div>
          </div>

          <button
            className="btn"
            onClick={() => { fetchRegs(); fetchFinance(); }}
            disabled={loadingRegs || loadingFin}
          >
            Refresh
          </button>
        </div>

        {(errorRegs || errorFin) ? (
          <div className="error" style={{ marginTop: 10 }}>
            {errorRegs || errorFin}
          </div>
        ) : null}

        <div
          style={{
            marginTop: 12,
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: 10
          }}
        >
          <div style={{ border: '1px solid #eee', borderRadius: 12, padding: 12, background: '#fff' }}>
            <div className="muted" style={{ fontSize: 12 }}>Registered</div>
            <div style={{ fontSize: 36, fontWeight: 900, marginTop: 6 }}>
              {loadingRegs ? '…' : stats.totalRegistered}
            </div>
          </div>

          <div style={{ border: '1px solid #eee', borderRadius: 12, padding: 12, background: '#fff' }}>
            <div className="muted" style={{ fontSize: 12 }}>Needs assistance</div>
            <div style={{ fontSize: 36, fontWeight: 900, marginTop: 6 }}>
              {loadingRegs ? '…' : stats.needAssist}
            </div>
          </div>

          <div style={{ border: '1px solid #eee', borderRadius: 12, padding: 12, background: '#fff' }}>
            <div className="muted" style={{ fontSize: 12 }}>Income</div>
            <div style={{ fontSize: 22, fontWeight: 800, marginTop: 6 }}>
              {loadingFin ? '…' : stats.fmtUsd(stats.totalIncome)}
            </div>
          </div>

          <div style={{ border: '1px solid #eee', borderRadius: 12, padding: 12, background: '#fff' }}>
            <div className="muted" style={{ fontSize: 12 }}>Expenses</div>
            <div style={{ fontSize: 22, fontWeight: 800, marginTop: 6 }}>
              {loadingFin ? '…' : stats.fmtUsd(stats.totalExpense)}
            </div>
          </div>

          <div style={{ border: '1px solid #eee', borderRadius: 12, padding: 12, background: '#fff' }}>
            <div className="muted" style={{ fontSize: 12 }}>Net</div>
            <div style={{ fontSize: 22, fontWeight: 900, marginTop: 6, color: stats.net < 0 ? '#b42318' : '#067647' }}>
              {loadingFin ? '…' : stats.fmtUsd(stats.net)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}